import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import Title from './Title'
import HeaderStatusInfo from './HeaderStatusInfo'
import Loading from './Loading'

import useGncHistory from '../../../hooks/useGncHistory'
import useSharepointToken from '../../../hooks/useSharepointToken'

/**
 * Shared SharePoint upload component for Documents and Records
 * 
 * @param {Object} props
 * @param {string} props.entityType - 'Document' or 'Record'
 * @param {string} props.apiPath - API path segment, e.g. 'documents' or 'records'
 * @param {string} props.statusField - Field name for status in API response, e.g. 'document_status' or 'record_status'
 */
const SharepointUpload = ({ entityType, apiPath, statusField }) => {
    const params = useParams();
    const isNew = params.objID === 'new';
    const objID = Number(params.objID);

    const { encryptionKey, tokenIsValid, redirectToAuthUrl } = useSharepointToken()
    const {
        goBack,
    } = useGncHistory()

    const [id, setID] = useState(objID)
    const [reference, setReference] = useState('')
    const [title, setTitle] = useState('')
    const [statusName, setStatusName] = useState('')
    
    const [hasLoaded, setHasLoaded] = useState(false)
    const [hasSaved, setHasSaved] = useState(false)

    const [ isUploading, setIsUploading ] = useState(false)
    const [ uploadResult, setUploadResult ] = useState(null)
    const [ uploadError, setUploadError ] = useState(null)
    
    useEffect(() => {
        if (isNew) {
            return
        }
        fetchObject()
    }, [])
    
    const fetchObject = () => {
        fetch(`/v0/${apiPath}/${objID}`, { credentials: 'same-origin' })
            .then(function(response) {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error(response.statusText)
                }
            })
            .then(function(json) {
                if(json.error) {
                    throw new Error(json.error)
                }

                const obj = json.result
                setReference(obj.reference)
                setTitle(obj.title)
                setStatusName(obj[statusField]?.name)
                setHasLoaded(true)
            })
            .catch(err => {
                console.log(`${entityType} fetch error`, err)
            })
        
    }

    const handleAuthenticate = () => {
        redirectToAuthUrl()
    }

    const handleUpload = async () => {
        setIsUploading(true)
        setUploadResult(null)
        setUploadError(null)
        
        try {
            const response = await fetch(
                `/v0/${apiPath}/${objID}/upload_to_sharepoint?encryption_key=${encryptionKey}`,
                {
                    credentials: 'same-origin',
                    method: 'POST'
                }
            )
                
            if (!response.ok) {
                const errorData = await response.json()
                throw new Error(errorData.error || 'Upload failed')
            }
            
            const json = await response.json()
            console.log("Set upload result", json.result)
            setUploadResult(json.result)
        } catch (error) {
            setUploadError(error.message)
        } finally {
            setIsUploading(false)
            setHasSaved(true)
        }
    }

    const handleOpenFile = () => {
        const obj = uploadResult
        const path = obj.file_paths?.[0]
        window.open(path, "_blank", "noopener,noreferrer")
    }
    
    const pageContent = useMemo(() => {
        if (!hasLoaded) {
            return <Loading inline={true}/>
        }

        if (!tokenIsValid) {
            return (
                <>
                <p>
                    To save the {entityType.toLowerCase()} to Sharepoint, you must first authenticate.
                </p>
                <button 
                    className="btn btn-primary" 
                    onClick={handleAuthenticate}
                >
                    Authenticate via Sharepoint
                </button>
                </>
            )
        }
        
        if (hasSaved) {
            return (
                <>
                <p>
                    The {entityType.toLowerCase()} has been saved in Sharepoint!
                </p>
                <button 
                    className="btn btn-primary" 
                    onClick={handleOpenFile}
                >
                    Open the {entityType}
                </button>
                </>
            )
            
        }
        
        return (
            <>
            <p>
                To save the {entityType.toLowerCase()} to Sharepoint, click the button below
            </p>
            <button 
                className="btn btn-primary" 
                onClick={handleUpload}
                disabled={isUploading}
            >
                {isUploading ?
                 <><i className="fa fa-refresh fa-spin fa-fw"></i> Uploading...</>
                 : 
                     <><i className="fa fa-cloud-upload"></i> Upload to SharePoint</>
                }
            </button>
            
            {uploadResult &&
             <span className="text-success" style={{marginLeft: '10px'}}>
                 <i className="fa fa-check"></i> Uploaded successfully
             </span>
            }
            
            {uploadError && (
                 <span className="text-danger" style={{marginLeft: '10px'}}>
                     <i className="fa fa-exclamation-triangle"></i> {uploadError}
                 </span>
             )}
            </>
        )
    }, [hasLoaded, tokenIsValid, hasSaved, isUploading, uploadResult, uploadError, entityType])
    
    return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            
                            <HeaderStatusInfo
                                type={entityType}
                                id={id}
                                status={statusName}
                            />

                            <Title
                                type={entityType}
                                reference={reference}
                                title={title}
                                id={id}
                                showLatest={true}
                            />
                        </div>
                        <div className="ibox-content">
                            {pageContent}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
};

export default SharepointUpload
